import type { Escopo } from './escopo';
import { ehDoEscopo } from './escopo';
import type { LinhaDoMes } from './mesFinanceiro';

/**
 * Um agendado (schedule) candidato a aparecer em "Contas do mês" — já
 * resolvido pelo CHAMADOR (ver `inicio/useAgendadosDoMes.ts`).
 *
 * `montarContasFixas` é PURO de propósito: não decide QUAIS agendados
 * mostrar nem DE ONDE sai `valorPrevisto` — só cruza a lista com as
 * transações do mês pra saber quem já pagou. Isso é o que faz este módulo
 * ser testável linha a linha sem tocar em `aqlQuery`.
 */
export type AgendadoDoMes = {
  id: string;
  nome: string;
  /** Valor orçado para o mês, em centavos e positivo. */
  valorPrevisto: number;
  /**
   * `next_date` do agendado (`YYYY-MM-DD`), ou `null` quando ele não tem
   * data — alimenta a faixa "Precisa agora" da tela Pendências
   * (`motor/filaPendencias.ts`), que precisa saber se a fixa não paga já
   * VENCEU.
   *
   * ⚠️ OBRIGATÓRIO de propósito (mesma lição do `escopo` obrigatório, FIX
   * ROUND 2 abaixo): opcional, um chamador que esquecesse de mapeá-lo faria
   * TODA fixa vencida sumir da fila em silêncio — typecheck verde, suíte
   * verde, tela mentindo. Quem não tem a data escreve `null`.
   */
  vencimento: string | null;
};

export type ContaFixa = {
  id: string;
  nome: string;
  planejado: number;
  /** null = ainda não paga neste mês. */
  realizado: number | null;
  /** Ver `AgendadoDoMes.vencimento`. */
  vencimento: string | null;
};

/**
 * ⭐⭐ A descoberta que dispensa código (ver o brief da Task 10): o Actual já
 * guarda, em `transactions.schedule`, o agendado que originou a transação.
 * Conta fixa PAGA = existe alguma `LinhaDoMes` deste mês com
 * `schedule === agendado.id`; conta fixa NÃO PAGA = nenhuma. Não precisamos
 * de uma coluna manual de "paguei/não paguei" — no FinApp antigo isso era
 * um campo digitado à mão e 157 dos 173 gastos de agosto/2026 estavam sem
 * marcar (medido em 25/08/2026).
 *
 * Soma TODOS os pagamentos do mês vinculados ao mesmo agendado (uma conta
 * fixa pode ter sido paga em duas parcelas, por exemplo) — nunca fica só
 * com o último.
 *
 * ⚠️⚠️ FIX ROUND 1 (Finding 1, Critical) — `escopo` filtra as LINHAS antes
 * de somar o realizado. Sem isso, uma transação `#empresa` contava como
 * pagamento de uma conta fixa mesmo olhando o escopo `'pessoal'` (e
 * vice-versa) — o mesmo princípio de `resumirMes` (a tag decide de quem é
 * o dinheiro), aplicado aqui porque duas linhas do MESMO agendado podem
 * carregar notas diferentes (uma pessoa marca uma parcela e esquece a
 * outra — mesma razão pela qual `mesFinanceiro.ts` trata `notes` por
 * PERNA de transferência, não por evento inteiro, quando não há como
 * saber que as duas concordam).
 *
 * ⚠️⚠️ FIX ROUND 2 (Finding 4, Important) — `escopo` é OBRIGATÓRIO, não
 * mais opcional com default `'tudo'`. O re-revisor provou que o default
 * era um SILENCIADOR: removendo o 3º argumento na chamada de `Inicio.tsx`,
 * typecheck passava limpo e os 145 testes continuavam verdes — ou seja,
 * um refactor futuro que esquecesse de passar `escopo` faria o Finding 1
 * (crítico) voltar SEM QUE NINGUÉM PERCEBESSE, nem no build nem na
 * suíte. Tornar obrigatório move essa rede pro COMPILADOR: qualquer
 * chamador que omitir o argumento agora quebra o `tsc`, não depende de
 * alguém lembrar de escrever o teste certo. Quem realmente precisar do
 * comportamento antigo (nenhum filtro) passa `'tudo'` EXPLICITAMENTE —
 * vira escolha escrita, não esquecimento.
 */
export function montarContasFixas(
  agendados: AgendadoDoMes[],
  linhas: LinhaDoMes[],
  escopo: Escopo,
): ContaFixa[] {
  const pagoPorAgendado = new Map<string, number>();
  for (const l of linhas) {
    if (!l.schedule || l.amount >= 0) continue;
    if (!ehDoEscopo(l.notes, escopo)) continue;
    pagoPorAgendado.set(
      l.schedule,
      (pagoPorAgendado.get(l.schedule) ?? 0) + -l.amount,
    );
  }

  return agendados.map(a => ({
    id: a.id,
    nome: a.nome,
    planejado: a.valorPrevisto,
    realizado: pagoPorAgendado.get(a.id) ?? null,
    vencimento: a.vencimento,
  }));
}

/**
 * ⭐ REGRA 2 do motor (`mesFinanceiro.ts`) virando código: fixa NÃO paga
 * entra na conta do "livre" pelo PLANEJADO; fixa PAGA já está contada em
 * `resumo.fixasRealizadas` (via `schedule` na própria `LinhaDoMes`) —
 * somar o planejado dela de novo aqui duplicaria o gasto. Sem essa
 * distinção, o "livre" incharia no começo do mês (nada pago ainda, tudo
 * "sobrando") e despencaria de repente no dia do vencimento.
 *
 * Alimenta `Extras.fixasPrevistas` de `resumirMes` — ver `Inicio.tsx` (o
 * ponto exato onde isso entra na conta do herói).
 */
export function totalPrevisto(contas: ContaFixa[]): number {
  return contas
    .filter(c => c.realizado === null)
    .reduce((soma, c) => soma + c.planejado, 0);
}
